import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

type Tone = 'info' | 'success' | 'warning' | 'danger';

interface Toast {
  id: number;
  tone: Tone;
  title?: string;
  message: string;
  ttl: number;
}

interface ToastContextValue {
  push: (t: Omit<Toast, 'id' | 'ttl'> & { ttl?: number }) => void;
  success: (msg: string, title?: string) => void;
  error: (msg: string, title?: string) => void;
  info: (msg: string, title?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('ToastProvider missing');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (t: Omit<Toast, 'id' | 'ttl'> & { ttl?: number }) => {
      const id = Date.now() + Math.random();
      const ttl = t.ttl ?? (t.tone === 'danger' ? 7000 : 5000);
      setToasts((prev) => [...prev.slice(-4), { ...t, id, ttl }]);
      setTimeout(() => remove(id), ttl);
    },
    [remove],
  );

  const ctx: ToastContextValue = {
    push,
    success: (message, title) => push({ tone: 'success', message, title }),
    error: (message, title) => push({ tone: 'danger', message, title }),
    info: (message, title) => push({ tone: 'info', message, title }),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}
      <div className="fixed bottom-3 left-3 right-3 sm:left-auto sm:bottom-4 sm:right-4 z-50 flex flex-col gap-2 sm:max-w-sm">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`card !p-4 flex items-start gap-3 animate-[fade_160ms_ease-out] ${
              t.tone === 'success'
                ? 'border-status-success/40'
                : t.tone === 'danger'
                  ? 'border-status-danger/40'
                  : t.tone === 'warning'
                    ? 'border-status-warning/40'
                    : 'border-status-info/40'
            }`}
          >
            <div className="flex-1">
              {t.title && <div className="text-body font-semibold mb-0.5">{t.title}</div>}
              <div className="text-small text-ink-secondary">{t.message}</div>
            </div>
            <button onClick={() => remove(t.id)} className="text-ink-muted hover:text-ink-primary text-small">
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
